// The reader's colour choices.
//
// A preference, not a fact about the data: two people reading the same
// dashboard are allowed to disagree about its colours. Stored per browser.
//
// What it owns: light/dark/system, the accent, the two device colours, and one
// colour per hotel slot. docs/CHARTS.md "Colours are the reader's choice too"
// is the specification; this is the implementation.
//
// `version` is the piece that matters to everything else. The palettes are
// plain values, not CSS, so a chart built against light tokens does not become
// a dark chart on its own. Every chart option watches `version` and rebuilds.

import { computed, reactive, ref, readonly } from 'vue'
import { PALETTES, onAccent, softStep } from '../lib/colour.js'
import { offeredAnywhere } from '../lib/customise.js'

const THEME_KEY = 'bw-dashboard-theme'
const THEME_MODES = ['system', 'light', 'dark']

/** Nothing chosen: every colour falls through to the stylesheet's own. */
const blank = () => ({ mode: 'system', accent: null, device: null, hotels: null })

/**
 * `localStorage` can throw, not merely come back empty — a browser with site
 * data blocked raises on access. A dashboard that will not paint because
 * someone declined cookies is a worse failure than losing a colour choice.
 */
function load() {
  try {
    const saved = JSON.parse(localStorage.getItem(THEME_KEY) ?? 'null')
    if (!saved || typeof saved !== 'object') return blank()
    return { ...blank(), ...saved, mode: THEME_MODES.includes(saved.mode) ? saved.mode : 'system' }
  } catch {
    return blank()
  }
}

function persist(next) {
  try {
    localStorage.setItem(THEME_KEY, JSON.stringify(next))
    return true
  } catch {
    return false
  }
}

const saved = reactive(load())
/** What the drawer is previewing. Edits preview live and only Save writes. */
const draft = reactive(load())
const version = ref(0)

const css = (name) =>
  typeof getComputedStyle === 'function'
    ? getComputedStyle(document.documentElement).getPropertyValue(name).trim()
    : ''

/**
 * Is the page dark right now?
 *
 * An explicit choice wins over the OS, because the drawer offers one. Read
 * fresh rather than captured at load: the palettes are rebuilt when the mode
 * changes, and a cached answer would leave dark bars on a light page.
 */
function isDark() {
  const chosen = document.documentElement.dataset.theme
  if (chosen) return chosen === 'dark'
  return typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches
}

/**
 * Push a theme into the document.
 *
 * The mode goes on `<html>` as `data-theme`, so CSS decides; `system` removes
 * the attribute rather than guessing. The colours are inline custom properties,
 * so they beat the stylesheet without it having to know they exist — and
 * clearing one removes the property rather than writing the default back as a
 * copy, which would freeze today's default into the reader's browser.
 */
function apply(theme) {
  const root = document.documentElement
  if (theme.mode === 'system') delete root.dataset.theme
  else root.dataset.theme = theme.mode

  // A reader-chosen accent applies only while some page still offers one
  // (lib/customise.js). None does in phase 1: the accent is tripla's magenta
  // (decision 0025). The stored choice is kept, not deleted, for the day the
  // control comes back.
  const own = offeredAnywhere('accent') ? theme.accent : null
  if (own) root.style.setProperty('--accent', own)
  else root.style.removeProperty('--accent')

  // The text colour on an accent fill. The stylesheet carries one per theme;
  // an inline one is written only for the reader's own accent. Written for the
  // stylesheet's accent too, it kept the value of the theme it was computed in,
  // and after the OS switched between light and dark it was the wrong one.
  if (own) root.style.setProperty('--on-accent', onAccent(own))
  else root.style.removeProperty('--on-accent')
  version.value += 1
}

/** Follow the OS while the mode is `system`. */
let watching = false
function watchSystem() {
  if (watching || typeof matchMedia !== 'function') return
  watching = true
  matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (draft.mode === 'system') version.value += 1
  })
}

export function useTheme() {
  watchSystem()

  /** The six chart hues, with any the reader replaced swapped in. */
  const palette = computed(() => {
    version.value // read, not used: this is the dependency that rebuilds on a theme change
    const dark = isDark()
    const base = PALETTES[dark ? 'dark' : 'light']
    const surface = css('--surface')
    return base.map((pair, i) => {
      const chosen = draft.hotels?.[i]
      return chosen ? [chosen, softStep(chosen, surface, dark)] : pair
    })
  })

  /** Mobile and desktop. Their own entry, because they are compared constantly
   *  and must not drift as hotels are added to the palette above. */
  const device = computed(() => {
    // Read for the same reason `palette` reads it: `isDark()` and `css()` are
    // not reactive, so without this a mode switch left both device colours on
    // the old theme until something else happened to recompute them.
    version.value
    const dark = isDark()
    const base = PALETTES[dark ? 'dark' : 'light']
    const surface = css('--surface')
    return {
      mobile: draft.device?.mobile
        ? [draft.device.mobile, softStep(draft.device.mobile, surface, dark)]
        : base[0],
      desktop: draft.device?.desktop
        ? [draft.device.desktop, softStep(draft.device.desktop, surface, dark)]
        : base[1],
    }
  })

  const deviceColor = (which, soft = false) => device.value[which][soft ? 1 : 0]

  const accent = computed(() => {
    version.value // see `device`: css() is not reactive on its own
    return (offeredAnywhere('accent') ? draft.accent : null) ?? (css('--accent') || '#bd1d58')
  })

  /**
   * Every change is kept the moment it is made: the drawer has no Save button,
   * and closing it keeps what is on screen. False when the browser refuses to
   * store it, so the drawer can say so; the change still shows for this visit.
   */
  function keep() {
    Object.assign(saved, JSON.parse(JSON.stringify(draft)))
    return persist(saved)
  }

  function setMode(mode) {
    if (!THEME_MODES.includes(mode)) return true
    draft.mode = mode
    apply(draft)
    return keep()
  }

  function setColour(path, value) {
    if (path === 'accent') draft.accent = value || null
    else if (path.startsWith('device.')) {
      draft.device = { ...(draft.device ?? {}), [path.slice(7)]: value || undefined }
    } else if (path.startsWith('hotel.')) {
      const list = [...(draft.hotels ?? [])]
      list[Number(path.slice(6))] = value || undefined
      draft.hotels = list
    }
    apply(draft)
    return keep()
  }

  function reset() {
    Object.assign(draft, blank())
    Object.assign(saved, blank())
    try {
      localStorage.removeItem(THEME_KEY)
    } catch {
      /* nothing stored */
    }
    document.documentElement.style.removeProperty('--accent')
    apply(draft)
  }

  return {
    theme: readonly(draft),
    version: readonly(version),
    palette,
    device,
    deviceColor,
    accent,
    isDark,
    css,
    setMode,
    setColour,
    reset,
    MODES: THEME_MODES,
  }
}

/** Called once at boot, before the first chart is built. */
export function initTheme() {
  apply(draft)
}

// Module-level singleton state: a hot update would hand new refs to some
// components while others keep the old ones, and the page would half-work until
// a manual reload. Escalate the update to a full reload instead.
if (import.meta.hot) {
  import.meta.hot.accept(() => import.meta.hot.invalidate())
}
